import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Reveal, RevealLines, easeSmooth } from "@/lib/motion";
import { ArrowUpRight, MapPin, Phone, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const projectTypes = ["Residential", "Commercial", "Heritage / Restoration", "Not sure yet"];

const initial = {
  name: "",
  email: "",
  phone: "",
  project_type: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete name, email and message.");
      return;
    }
    try {
      setSending(true);
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm(initial);
      toast.success("Enquiry received — we’ll be in touch shortly.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Unable to send.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-28 md:pt-32">
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-12">
        <span className="eyebrow">Enquiries</span>
        <div className="mt-6">
          <RevealLines
            className="font-display text-[12vw] md:text-[9vw] leading-[0.9] tracking-tighter"
            lines={[
              <>Let’s build</>,
              <><em className="hero-italic">something quiet.</em></>,
            ]}
            stagger={0.14}
          />
        </div>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-28 grid grid-cols-1 md:grid-cols-12 gap-10">
        {/* LEFT — contact info */}
        <aside className="md:col-span-4 md:pr-8 border-t border-[#D9D4C7] pt-8" data-testid="contact-info">
          <Reveal>
            <p className="font-display text-2xl md:text-3xl leading-[1.2] tracking-tight text-[#1A1A1A] max-w-sm">
              We reply to every enquiry within one business day — usually the same afternoon.
            </p>

            <ul className="mt-10 space-y-6">
              <li>
                <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#4A4A4A] mb-2">
                  Studio
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="w-4 h-4 text-[#8C7A6B]" />
                  <span className="font-grotesk text-[15px]">London, Ontario · Canada</span>
                </div>
              </li>
              <li>
                <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#4A4A4A] mb-2">
                  Phone
                </div>
                <a href="tel:+15197029417" className="flex items-center gap-3 link-under" data-testid="contact-phone">
                  <Phone className="w-4 h-4 text-[#8C7A6B]" />
                  <span className="font-grotesk text-[15px]">+1 519-702-9417</span>
                </a>
              </li>
              <li>
                <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#4A4A4A] mb-2">
                  Email
                </div>
                <a
                  href="mailto:Yachconstruction1@gmail.com"
                  className="flex items-center gap-3 link-under"
                  data-testid="contact-email"
                >
                  <Mail className="w-4 h-4 text-[#8C7A6B]" />
                  <span className="font-grotesk text-[15px]">Yachconstruction1@gmail.com</span>
                </a>
              </li>
              <li>
                <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#4A4A4A] mb-2">
                  Hours
                </div>
                <div className="font-grotesk text-[15px] leading-[1.7]">
                  Mon – Fri · 08:00 – 18:00<br />
                  Saturday · By appointment
                </div>
              </li>
            </ul>
          </Reveal>
        </aside>

        {/* RIGHT — form */}
        <div className="md:col-span-8 md:pl-8 md:border-l border-t md:border-t-0 border-[#D9D4C7] pt-8 md:pt-0" data-testid="contact-form-wrap">
          {sent ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: easeSmooth }}
              className="min-h-[420px] flex flex-col justify-center"
            >
              <span className="eyebrow">Enquiry Received</span>
              <h3 className="mt-6 font-display text-5xl md:text-6xl leading-[0.95] tracking-tight">
                Thank you.
              </h3>
              <p className="mt-6 max-w-lg font-grotesk text-[15px] text-[#4A4A4A] leading-[1.7]">
                We have your note. A principal from the studio will reply personally within one
                business day. If it’s urgent, do call us at +1 519-702-9417.
              </p>
              <button
                onClick={() => setSent(false)}
                className="btn-ghost mt-10 self-start"
                data-testid="send-another"
              >
                <span>Send another enquiry</span>
                <span>→</span>
              </button>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="space-y-2" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                <Field label="Full name" required>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setField("name", e.target.value)}
                    data-testid="input-name"
                    autoComplete="name"
                    required
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setField("email", e.target.value)}
                    data-testid="input-email"
                    autoComplete="email"
                    required
                  />
                </Field>
                <Field label="Phone (optional)">
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setField("phone", e.target.value)}
                    data-testid="input-phone"
                    autoComplete="tel"
                  />
                </Field>
                <SelectField
                  label="Project type"
                  value={form.project_type}
                  onChange={(v) => setField("project_type", v)}
                  options={projectTypes}
                  testid="input-project-type"
                  className="md:col-span-2"
                />
                <Field label="Tell us about the project" required className="md:col-span-2">
                  <textarea
                    rows={5}
                    value={form.message}
                    onChange={(e) => setField("message", e.target.value)}
                    data-testid="input-message"
                    required
                  />
                </Field>
              </div>

              <div className="pt-10 flex items-center justify-between gap-4 flex-wrap">
                <p className="font-grotesk text-[11px] uppercase tracking-[0.22em] text-[#4A4A4A] max-w-sm">
                  By submitting you agree to be contacted about your enquiry. We never share your details.
                </p>
                <button type="submit" className="btn-sharp" disabled={sending} data-testid="submit-contact">
                  <span>{sending ? "Sending…" : "Send Enquiry"}</span>
                  <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}

function Field({ label, children, required, className = "" }) {
  const child = children;
  const filled =
    child?.props?.value !== undefined && String(child.props.value).length > 0;
  return (
    <div className={`field ${filled ? "filled" : ""} ${className}`}>
      {children}
      <label>
        {label}
        {required ? " *" : ""}
      </label>
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid, className = "" }) {
  const filled = value && value.length > 0;
  return (
    <div className={`field ${filled ? "filled" : ""} ${className}`}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      >
        <option value="" />
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
      <label>{label}</label>
    </div>
  );
}
